import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, Check, Search, Mail, RefreshCw, Inbox } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInbox() {
  const { logout, authHeader } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | unread

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: authHeader });
      setEnquiries(data);
    } catch {
      toast.error("Failed to load enquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/enquiries/${id}/read`, {}, { headers: authHeader });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, read: true } : e)));
    } catch { toast.error("Could not update."); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, { headers: authHeader });
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted.");
    } catch { toast.error("Could not delete."); }
  };

  const filtered = useMemo(() => {
    return enquiries.filter((e) => {
      if (filter === "unread" && e.read) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return [e.name, e.email, e.company, e.interest, e.message].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [enquiries, query, filter]);

  const unread = enquiries.filter((e) => !e.read).length;

  return (
    <div className="min-h-screen bg-bg">
      <div className="grain" aria-hidden="true" />
      <header className="sticky top-0 z-40 bg-surface/90 backdrop-blur-2xl border-b border-ink/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-ink"><Logo /></Link>
            <div className="hidden md:block border-l border-ink/15 pl-6"><div className="overline text-brand-green">Enquiry Inbox</div><div className="font-heading text-ink text-sm">{enquiries.length} total · {unread} unread</div></div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh-btn" className="p-3 border border-ink/15 text-ink/70 hover:text-ink hover:border-ink/40 transition-colors" aria-label="Refresh"><RefreshCw size={16} /></button>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 font-heading text-xs font-semibold border border-ink/15 text-ink/80 px-5 py-3 hover:bg-ink hover:text-white transition-colors"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-8">
          <div className="relative w-full md:max-w-sm">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/30" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="admin-search" placeholder="Search name, email, company…" className="w-full bg-surface border border-ink/10 focus:border-brand-green pl-11 pr-4 py-3 font-body text-sm text-ink placeholder:text-ink/25 outline-none transition-colors" />
          </div>
          <div className="flex gap-px bg-ink/10 border border-ink/10">
            {["all", "unread"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`} className={`font-heading text-xs font-semibold uppercase tracking-wide px-6 py-3 transition-colors ${filter === f ? "bg-brand-green text-white" : "bg-surface text-ink/60 hover:text-ink"}`}>{f}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-32 text-center text-ink/40 font-body" data-testid="admin-loading">Loading enquiries…</div>
        ) : filtered.length === 0 ? (
          <div className="py-32 text-center" data-testid="admin-empty">
            <Inbox size={40} className="mx-auto text-ink/20" />
            <p className="mt-6 font-heading text-ink/50">No enquiries {filter === "unread" ? "unread" : "yet"}.</p>
          </div>
        ) : (
          <div className="grid gap-px bg-ink/10 border border-ink/10" data-testid="admin-enquiry-list">
            {filtered.map((e) => (
              <div key={e.id} className={`bg-surface p-6 md:p-8 ${!e.read ? "border-l-2 border-l-brand-green" : ""}`} data-testid={`admin-enquiry-${e.id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-bold text-ink text-lg">{e.name}</h3>
                      {!e.read && <span className="overline text-[9px] text-brand-green border border-brand-green/40 px-2 py-0.5">New</span>}
                      <span className="overline text-ink/30 text-[9px]">{new Date(e.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 font-body text-sm text-ink/60">
                      <a href={`mailto:${e.email}`} className="hover:text-brand-green transition-colors">{e.email}</a>
                      {e.phone && <span>{e.phone}</span>}
                      {e.company && <span>{e.company}</span>}
                    </div>
                    {e.interest && <div className="mt-3 inline-block overline text-ink/50 border border-ink/10 px-3 py-1.5">{e.interest}</div>}
                    <p className="mt-4 font-body text-sm text-ink/75 leading-relaxed max-w-3xl">{e.message}</p>
                  </div>
                  <div className="flex md:flex-col gap-2 shrink-0">
                    <a href={`mailto:${e.email}`} className="inline-flex items-center justify-center gap-2 font-heading text-xs border border-ink/15 text-ink/80 px-4 py-2.5 hover:bg-ink hover:text-white transition-colors" data-testid={`admin-reply-${e.id}`}><Mail size={13} /> Reply</a>
                    {!e.read && <button onClick={() => markRead(e.id)} className="inline-flex items-center justify-center gap-2 font-heading text-xs border border-ink/15 text-ink/80 px-4 py-2.5 hover:bg-ink hover:text-white transition-colors" data-testid={`admin-read-${e.id}`}><Check size={13} /> Read</button>}
                    <button onClick={() => remove(e.id)} className="inline-flex items-center justify-center gap-2 font-heading text-xs border border-brand-green/40 text-brand-green px-4 py-2.5 hover:bg-brand-green hover:text-ink transition-colors" data-testid={`admin-delete-${e.id}`}><Trash2 size={13} /> Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
